import type { Breakpoint } from "@mui/material/styles";

import {
  AppBar,
  AppBarProps,
  Box,
  Container,
  ContainerProps,
  Toolbar,
  ToolbarProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { bgBlur } from "app/helpers/css";
import { createColorWithAlphaChannel } from "app/theme/helpers/createColorWithAlphaChannel";

import { layoutClasses } from "../settings";

export type HeaderSectionProps = AppBarProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    container?: ContainerProps;
    toolbar?: ToolbarProps;
  };
  slots?: {
    bottomArea?: React.ReactNode;
    centerArea?: React.ReactNode;
    leftArea?: React.ReactNode;
    rightArea?: React.ReactNode;
    topArea?: React.ReactNode;
  };
};

export function HeaderSection({
  layoutQuery = "md",
  slotProps,
  slots,
  sx,
  ...other
}: HeaderSectionProps) {
  const theme = useTheme();

  const toolbarStyles = {
    default: {
      ...bgBlur({
        color: createColorWithAlphaChannel(
          theme.vars.palette.background.defaultChannel,
          0.8,
        ),
      }),
      height: "var(--layout-header-mobile-height)",
      minHeight: "auto",
      [theme.breakpoints.up("sm")]: {
        minHeight: "auto",
      },
      [theme.breakpoints.up(layoutQuery)]: {
        height: "var(--layout-header-desktop-height)",
      },
      transition: theme.transitions.create(["height", "background-color"], {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
  };

  return (
    <AppBar
      className={layoutClasses.header}
      color="transparent"
      position="sticky"
      sx={{
        boxShadow: "none",
        zIndex: "var(--layout-header-zIndex)",
        ...sx,
      }}
      {...other}
    >
      {slots?.topArea}
      <Toolbar
        disableGutters
        {...slotProps?.toolbar}
        sx={{
          ...toolbarStyles.default,
          ...slotProps?.toolbar?.sx,
        }}
      >
        <Container
          {...slotProps?.container}
          sx={{
            alignItems: "center",
            display: "flex",
            height: 1,
            ...slotProps?.container?.sx,
          }}
        >
          {slots?.leftArea}

          <Box
            sx={{ display: "flex", flex: "1 1 auto", justifyContent: "center" }}
          >
            {slots?.centerArea}
          </Box>

          {slots?.rightArea}
        </Container>
      </Toolbar>
      {slots?.bottomArea}
    </AppBar>
  );
}
